/**
 * Create/edit drawer for a scheduled site event (drill / training / field
 * exercise on the operating timeline).
 *
 * Recurrence is offered as a simple frequency picker that serializes to the
 * `{ rrule: "FREQ=…" }` shape the backend already consumes (the same shape as
 * `DrillSchedule.recurrence_rule`); "Does not repeat" sends null.
 */

import { useEffect } from 'react';
import { Field, SimpleGrid, VStack } from '@chakra-ui/react';
import {
  Button as ButtonComponent,
  DrawerComponent,
  InputField,
  SelectInput,
  TextareaField,
} from '@themes/aegix';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  scheduledSiteEventFormSchema,
  type ScheduledSiteEventFormValues,
} from '../../../validations/whakahaereConfig.validation';
import {
  SITE_EVENT_TYPE_LABELS,
  type ScheduledSiteEvent,
  type SiteEventType,
} from '../../../services';
import {
  useCreateScheduledSiteEvent,
  useUpdateScheduledSiteEvent,
} from '../../../hooks/queries';
import { toaster } from '../../ui/toaster';

interface ScheduledEventDrawerProps {
  calendarId: number;
  isOpen: boolean;
  row: ScheduledSiteEvent | null;
  onClose: () => void;
  onDelete: (id: number) => void;
}

const EVENT_TYPE_OPTIONS = (
  Object.keys(SITE_EVENT_TYPE_LABELS) as SiteEventType[]
).map((t) => ({ value: t, label: SITE_EVENT_TYPE_LABELS[t] }));

const RECURRENCE_OPTIONS = [
  { value: 'none', label: 'Does not repeat' },
  { value: 'FREQ=DAILY', label: 'Daily' },
  { value: 'FREQ=WEEKLY', label: 'Weekly' },
  { value: 'FREQ=MONTHLY', label: 'Monthly' },
];

/** ISO 8601 → `datetime-local` value (`YYYY-MM-DDTHH:MM`, local time). */
function isoToLocalInput(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

/** Pull a `FREQ=…` token out of the stored recurrence_rule for the picker. */
function recurrenceToOption(
  rule: ScheduledSiteEvent['recurrence_rule'],
): string {
  if (!rule) return 'none';
  const text =
    typeof rule === 'string'
      ? rule
      : typeof rule === 'object' && 'rrule' in rule
        ? String((rule as { rrule: unknown }).rrule)
        : '';
  const match = text.match(/FREQ=(DAILY|WEEKLY|MONTHLY)/i);
  return match ? `FREQ=${match[1].toUpperCase()}` : 'none';
}

function defaults(row: ScheduledSiteEvent | null): ScheduledSiteEventFormValues {
  return {
    event_type: row?.event_type ?? 'drill',
    name: row?.name ?? '',
    description: row?.description ?? '',
    start: isoToLocalInput(row?.start),
    duration_minutes: row?.duration_minutes ?? 60,
    recurrence_rule: recurrenceToOption(row?.recurrence_rule),
  };
}

export function ScheduledEventDrawer({
  calendarId,
  isOpen,
  row,
  onClose,
  onDelete,
}: ScheduledEventDrawerProps) {
  const createEvent = useCreateScheduledSiteEvent();
  const updateEvent = useUpdateScheduledSiteEvent(calendarId);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ScheduledSiteEventFormValues>({
    defaultValues: defaults(row),
    resolver: zodResolver(scheduledSiteEventFormSchema),
    mode: 'onBlur',
  });

  useEffect(() => {
    if (isOpen) reset(defaults(row));
  }, [isOpen, row, reset]);

  const onSubmit = async (values: ScheduledSiteEventFormValues) => {
    const recurrence =
      values.recurrence_rule && values.recurrence_rule !== 'none'
        ? { rrule: values.recurrence_rule }
        : null;
    const payload = {
      calendar: calendarId,
      event_type: values.event_type as SiteEventType,
      name: values.name ?? '',
      description: values.description ?? '',
      // `datetime-local` is local wall time; serialize back to ISO.
      start: new Date(values.start).toISOString(),
      duration_minutes: values.duration_minutes,
      recurrence_rule: recurrence,
    };
    try {
      if (row) {
        await updateEvent.mutateAsync({ id: row.id, data: payload });
        toaster.create({ title: 'Event updated', type: 'success' });
      } else {
        await createEvent.mutateAsync(payload);
        toaster.create({ title: 'Event scheduled', type: 'success' });
      }
      onClose();
    } catch (err) {
      toaster.create({
        title: 'Could not save the event',
        description: err instanceof Error ? err.message : undefined,
        type: 'error',
      });
    }
  };

  return (
    <DrawerComponent
      isOpen={isOpen}
      onClose={onClose}
      title={row ? 'Edit event' : 'Schedule event'}
      size="md"
      closeOnOutside={false}
      footer={
        <SimpleGrid columns={row ? 3 : 2} gap={3} width="100%">
          {row && (
            <ButtonComponent
              visual="alert"
              type="button"
              onClick={() => onDelete(row.id)}
              data-e2e="delete-event-button"
            >
              Delete
            </ButtonComponent>
          )}
          <ButtonComponent
            visual="secondary"
            type="button"
            onClick={onClose}
            data-e2e="cancel-event-button"
          >
            Cancel
          </ButtonComponent>
          <ButtonComponent
            visual="primary"
            type="submit"
            form="scheduled-event-form"
            disabled={isSubmitting}
            data-e2e="save-event-button"
          >
            Save
          </ButtonComponent>
        </SimpleGrid>
      }
    >
      <form id="scheduled-event-form" onSubmit={handleSubmit(onSubmit)}>
        <VStack gap={4} align="stretch">
          <Field.Root invalid={!!errors.event_type}>
            <Field.Label htmlFor="event-type">Type</Field.Label>
            <Controller
              name="event_type"
              control={control}
              render={({ field }) => (
                <SelectInput
                  id="event-type"
                  value={field.value}
                  option={EVENT_TYPE_OPTIONS}
                  onChange={(val: string) => field.onChange(val)}
                  searchable={false}
                  e2eTitle="event-type-select"
                />
              )}
            />
            {errors.event_type && (
              <Field.ErrorText>{errors.event_type.message}</Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!errors.name}>
            <Field.Label htmlFor="event-name">Name</Field.Label>
            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <InputField
                  id="event-name"
                  placeholder="e.g. Q1 Fire Drill"
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value)}
                  data-e2e="event-name-input"
                />
              )}
            />
            {errors.name && (
              <Field.ErrorText>{errors.name.message}</Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!errors.start}>
            <Field.Label htmlFor="event-start">Starts</Field.Label>
            <Controller
              name="start"
              control={control}
              render={({ field }) => (
                <InputField
                  id="event-start"
                  type="datetime-local"
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}
                  data-e2e="event-start-input"
                />
              )}
            />
            {errors.start && (
              <Field.ErrorText>{errors.start.message}</Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!errors.duration_minutes}>
            <Field.Label htmlFor="event-duration">
              Duration (minutes)
            </Field.Label>
            <Controller
              name="duration_minutes"
              control={control}
              render={({ field }) => (
                <InputField
                  id="event-duration"
                  type="number"
                  min={1}
                  value={String(field.value)}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value === '' ? 0 : Number(e.target.value),
                    )
                  }
                  data-e2e="event-duration-input"
                />
              )}
            />
            {errors.duration_minutes && (
              <Field.ErrorText>
                {errors.duration_minutes.message}
              </Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!errors.recurrence_rule}>
            <Field.Label htmlFor="event-recurrence">Repeats</Field.Label>
            <Controller
              name="recurrence_rule"
              control={control}
              render={({ field }) => (
                <SelectInput
                  id="event-recurrence"
                  value={field.value ?? 'none'}
                  option={RECURRENCE_OPTIONS}
                  onChange={(val: string) => field.onChange(val)}
                  searchable={false}
                  e2eTitle="event-recurrence-select"
                />
              )}
            />
            {errors.recurrence_rule && (
              <Field.ErrorText>
                {errors.recurrence_rule.message}
              </Field.ErrorText>
            )}
          </Field.Root>

          <Field.Root invalid={!!errors.description}>
            <Field.Label htmlFor="event-description">Description</Field.Label>
            <Controller
              name="description"
              control={control}
              render={({ field }) => (
                <TextareaField
                  id="event-description"
                  placeholder="Optional instructions or notes"
                  value={field.value ?? ''}
                  onChange={(e) => field.onChange(e.target.value)}
                  rows={3}
                  data-e2e="event-description-input"
                />
              )}
            />
            {errors.description && (
              <Field.ErrorText>{errors.description.message}</Field.ErrorText>
            )}
          </Field.Root>
        </VStack>
      </form>
    </DrawerComponent>
  );
}
